// 권한 타입 정의
export type Permission =
  // 외부자
  | 'external'

  // 마케팅 관련
  | 'marketing'
  | 'curation'
  | 'events'
  | 'notifications'
  | 'banners'
  | 'feed'
  | 'deals-tab'
  | 'product-collections'

  // 세일즈 관련
  | 'sales'
  | 'products'
  | 'inventory'
  | 'orders'
  | 'settlements'
  | 'commission'

  // 운영 관련
  | 'operations'
  | 'schedules'
  | 'invoices'
  | 'chat'
  | 'user-contents'
  | 'lounge'
  | 'customer-service'

  // 개발팀 관련
  | 'development'
  | 'system'
  | 'monitoring'
  | 'utilities'
  | 'reports'

  // 사용자관리 관련
  | 'user-management'
  | 'partner-management'
  | 'memberships'
  | 'points'
  | 'coupons'

  // 베네피아 관련
  | 'benepia'
  | 'benepia-users'
  | 'benepia-payments'
  | 'benepia-products'
  | 'benepia-curation'
  | 'benepia-banners'

  // 예약성사 전용 (외부 검수 계정). schedules와 분리해 다른 운영 메뉴는 열리지 않는다.
  | 'reservation-success'

  // 슈퍼 어드민
  | 'admin-management'
  // 모든 권한
  | '*';

// 권한 그룹 정의
export const PERMISSION_GROUPS = {
  MARKETING: [
    'marketing',
    'curation',
    'events',
    'notifications',
    'banners',
    'feed',
    'deals-tab',
    'product-collections',
  ] as Permission[],
  SALES: ['sales', 'products', 'inventory', 'orders', 'settlements', 'commission'] as Permission[],
  OPERATIONS: [
    'operations',
    'schedules',
    'invoices',
    'chat',
    'user-contents',
    'lounge',
    'customer-service',
  ] as Permission[],
  DEVELOPMENT: ['development', 'system', 'monitoring', 'utilities', 'reports'] as Permission[],
  USER_MANAGEMENT: ['user-management', 'partner-management', 'memberships', 'points', 'coupons'] as Permission[],
  RESERVATION_SUCCESS: ['reservation-success'] as Permission[],
  SUPER_ADMIN: ['admin-management'] as Permission[],
} as const;

// 페이지별 권한 매핑
// 배열은 OR(하나라도 있으면 접근). 그룹 상위 권한(sales, user-management 등)을
// 하위 페이지에 같이 넣으면 하위 체크를 해제해도 메뉴가 남는다.
export const PAGE_PERMISSIONS: Record<string, Permission[]> = {
  // 대시보드
  '/dashboard': ['development', 'marketing', 'sales', 'operations', 'external'],

  // 보고서 (개발팀)
  '/report/products': ['reports'],
  '/report/product-exposure': ['reports'],
  '/report/sales': ['reports'],
  '/report/purchases': ['reports'],
  '/report/net-sales-report': ['admin-management'],

  // 유저관리
  '/users': ['user-management'],
  '/partner-users': ['partner-management'],
  '/admin-users': ['admin-management'],

  // 상품 관리 (세일즈)
  '/products': ['products'],
  '/products-inventory': ['inventory'],
  '/product-inventory-closing-history': ['inventory'],

  // 수강증 관리 (운영)
  '/class-invoices': ['invoices'],
  '/program-invoices': ['invoices'],

  // 프로그램 일정 관리 (운영)
  '/program-schedules': ['schedules'],
  '/program-schedule-groups': ['schedules'],
  '/program-reports': ['schedules'],
  '/grouping-agent-runs': ['schedules', 'reservation-success'],
  '/reservation-success-batch-runs': ['schedules', 'reservation-success'],
  '/reservation-success-product-indicators': ['schedules', 'reservation-success'],
  '/reservation-success-metrics': ['schedules', 'reservation-success'],
  '/reservation-success': ['schedules', 'reservation-success'],
  '/reservation-success-alerts': ['schedules', 'reservation-success'],
  '/group-monitoring': ['schedules', 'reservation-success'],
  '/last-chance-monitoring': ['schedules', 'deals-tab'],

  // 결제 관리 (세일즈)
  '/product-orders': ['orders'],
  '/payment-statements': ['orders'],
  '/payment-templates': ['orders'],
  '/settlements': ['settlements'],
  '/commission-rate-template': ['commission'],
  '/product-orders-refund-schedule': ['orders'],

  // 멤버십 관리 (사용자관리)
  '/memberships': ['memberships'],
  '/membership-users': ['memberships'],

  // 외부 판매 채널 (세일즈)
  '/naver-commerce-orders': ['orders'],
  '/naver-commerce-products': ['products'],

  // 푸시 & 카카오 알림 (마케팅)
  '/push-notifications': ['notifications'],
  '/kakao-notifications': ['notifications'],
  '/kakao-reserved-notifications': ['notifications'],

  // 상품 큐레이션 (마케팅)
  '/deals': ['curation', 'deals-tab'],
  '/time-sales': ['curation', 'deals-tab'],
  '/main-feed': ['curation', 'feed'],
  '/main-feed-v2': ['curation', 'feed'],
  '/campaigns': ['curation', 'feed'],
  '/product-collections': ['curation', 'product-collections'],
  '/customer-benefit-events': ['events'],
  '/banners': ['banners'],
  '/feed-elements': ['feed'],

  // 채팅 관리 (운영)
  '/chat-rooms': ['chat'],
  '/chat-messages': ['chat'],
  '/violation-detected-messages': ['chat'],
  '/chat-rooms-v2': ['chat'],

  // 고객 참여 관리 (운영)
  '/user-contents': ['user-contents', 'lounge'],
  '/comment-monitoring': ['user-contents'],
  '/review-events': ['user-contents'],
  '/customer-request': ['customer-service'],
  '/user-event-records': ['user-contents'],
  '/affiliates': ['user-contents'],

  // 포인트/쿠폰/이벤트 (사용자관리)
  '/discount-coupon': ['coupons'],
  '/user-discount-coupon': ['coupons'],
  '/point-template': ['points'],
  '/coupon': ['coupons'],
  '/coupon-log': ['coupons'],
  '/point-log': ['points'],
  '/join-promotion': ['coupons'],
  '/join-promotion-log': ['coupons'],
  '/events': ['events'],

  // 베네피아
  '/benepia/users': ['benepia-users'],
  '/benepia/banners': ['benepia-banners'],
  '/benepia/payments': ['benepia-payments'],
  '/benepia/products': ['benepia-products'],
  '/benepia/curation': ['benepia-curation'],

  // 시스템 관리 (개발팀)
  '/notices': ['system'],
  '/remote-config': ['system'],
  '/sqs-message-management': ['system'],
  '/naver-commerce-monitoring': ['monitoring'],
  '/payments': ['system'],
  '/product-clustering': ['system'],
  '/utility': ['utilities'],
  '/integration-apis': ['system'],
};

// 권한 확인 함수
export const hasPermission = (userPermissions: Permission[], requiredPermissions: Permission[]): boolean => {
  // 모든 권한(*)이 있으면 모든 페이지 접근 가능
  if (userPermissions.includes('*')) {
    return true;
  }

  // 필요한 권한 중 하나라도 있으면 접근 가능
  return requiredPermissions.some((permission) => userPermissions.includes(permission));
};

// 특별한 권한이 필요한 페이지들 (admin-management 권한이 추가로 필요)
const SPECIAL_PERMISSION_PAGES = ['/admin-users', '/report/net-sales-report'];

// 페이지 접근 권한 확인 함수
export const canAccessPage = (userPermissions: Permission[], path: string): boolean => {
  const requiredPermissions = PAGE_PERMISSIONS[path];

  if (!requiredPermissions) {
    // 권한이 정의되지 않은 페이지는 접근 불가
    return false;
  }

  // 특별한 권한이 필요한 페이지인 경우 admin-management 권한이 추가로 필요
  if (SPECIAL_PERMISSION_PAGES.includes(path)) {
    // admin-management 권한이 없으면 접근 불가 (["*"] 권한이 있어도)
    if (!userPermissions.includes('admin-management')) {
      return false;
    }
  }

  return hasPermission(userPermissions, requiredPermissions);
};

const DEFAULT_LANDING_CANDIDATES = [
  '/dashboard',
  '/program-schedules',
  '/reservation-success',
  '/products',
  '/product-orders',
  '/users',
  '/partner-users',
  '/class-invoices',
  '/push-notifications',
  '/deals',
  '/chat-rooms',
  '/user-contents',
  '/memberships',
  '/report/products',
  '/notices',
  '/benepia/users',
];

/** 로그인·권한 없는 경로에서 보낼 첫 화면. 대시보드 권한이 없으면 열 수 있는 메뉴로 보낸다. */
export const getDefaultLandingPath = (userPermissions: Permission[]): string => {
  const landing = DEFAULT_LANDING_CANDIDATES.find((path) => canAccessPage(userPermissions, path));
  return landing || '/login';
};

// 메뉴 아이템 필터링 함수 (재귀적으로 중첩 children 처리, 원본 객체 변이 없음)
export const filterMenuItemsByPermissions = (menuItems: any[], userPermissions: Permission[]): any[] => {
  return menuItems.reduce((acc: any[], item) => {
    // 로그아웃은 항상 표시
    if (item.key === 'logout') {
      return [...acc, item];
    }

    // 하위 메뉴가 있는 경우 재귀적으로 필터링
    if (item.children) {
      const filteredChildren = filterMenuItemsByPermissions(item.children, userPermissions);

      // 하위 메뉴가 하나라도 있으면 상위 메뉴 표시 (원본 변이 없이 새 객체 반환)
      if (filteredChildren.length > 0) {
        return [...acc, { ...item, children: filteredChildren }];
      }
      return acc;
    }

    // 단일 메뉴인 경우
    const path = item.label?.props?.to;
    if (path ? canAccessPage(userPermissions, path) : true) {
      return [...acc, item];
    }
    return acc;
  }, []);
};
